import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import Pagination from 'react-js-pagination';
import { marketingService } from '../../_services/marketing.service';
import './SettingsModern.css';
import { formatPence, creditsToPence } from '../pricing/money';

/**
 * Marketing credit purchases — the receipt trail for prepaid credit top-ups.
 *
 * Separate from Invoices because they're different instruments: invoices are the
 * monthly platform-fee bill, top-ups are one-off card charges that happen whenever
 * the salon (or auto-recharge) buys credits. Same table language so the tabs read
 * as one page.
 */
function CreditPurchases() {
    const [rows, setRows] = useState([]);
    const [notice, setNotice] = useState(null);
    const [isLoader, setIsLoader] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [activePage, setActivePage] = useState(1);
    const itemPerPageLimit = 10;

    useEffect(() => {
        let alive = true;
        setIsLoader(true);
        marketingService.smsWalletPurchases({ page: activePage, limit: itemPerPageLimit })
            .then((res) => {
                if (!alive) return;
                if (res?.data?.statusCode === 1) {
                    const data = res.data.responseData;
                    setRows(data?.result || []);
                    setTotalCount(data?.totalCount || 0);
                }
                setIsLoader(false);
            })
            .catch((e) => {
                if (!alive) return;
                // A stylist whose salon covers their marketing has no wallet of
                // their own — say so, rather than showing an empty table that
                // implies they simply haven't bought anything yet.
                const msg = e && e.response && e.response.data
                    && (e.response.data.message || (e.response.data.error && e.response.data.error.responseMessage));
                if (msg) setNotice(msg);
                setIsLoader(false);
            });
        return () => { alive = false; };
    }, [activePage]);

    if (isLoader) return null;

    const money = (n) => `£${Number(n || 0).toFixed(2)}`;
    // Pounds, never credits: pence fields when the API sends them, else credits × 2p.
    const addedOf = (row) => formatPence(row.addedPence != null ? row.addedPence : row.amountPence != null ? row.amountPence : creditsToPence(row.credits));
    const balanceAfterOf = (row) => formatPence(row.balanceAfterPence != null ? row.balanceAfterPence : creditsToPence(row.balanceAfter));

    return (
        <div className="settings-card">
            <div className="settings-card-header">
                <h2>Top-ups</h2>
                <p>Receipts for message balance top-ups on your card.</p>
            </div>

            {rows.length > 0 ? (
                <>
                    {/* Desktop table */}
                    <div className="bi-invoice-table-wrap">
                        <table className="settings-table cp-table">
                            <thead>
                                <tr>
                                    <th className="cp-col-date">Date</th>
                                    <th className="cp-num">Added</th>
                                    <th className="cp-num">Amount</th>
                                    <th className="cp-num">Balance after</th>
                                    <th style={{ width: 44 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr
                                        key={row._id}
                                        style={{ cursor: row.receiptUrl ? 'pointer' : 'default' }}
                                        onClick={() => row.receiptUrl && window.open(row.receiptUrl)}
                                    >
                                        <td className="cp-col-date" style={{ fontWeight: 500 }}>
                                            {moment(row.date).format('D MMM YYYY')}
                                            {row.automatic && <span className="cp-badge-auto">Auto</span>}
                                        </td>
                                        <td className="cp-num">{addedOf(row)}</td>
                                        <td className="cp-num" style={{ fontWeight: 600 }}>{money(row.amountGBP)}</td>
                                        <td className="cp-num">{balanceAfterOf(row)}</td>
                                        <td className="cp-col-receipt">
                                            {row.receiptUrl && (
                                                <span className="bi-download-btn">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                                        <polyline points="7 10 12 15 17 10" />
                                                        <line x1="12" y1="15" x2="12" y2="3" />
                                                    </svg>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="bi-invoice-mobile">
                        {rows.map((row) => (
                            <div
                                key={row._id}
                                className="bi-invoice-card"
                                onClick={() => row.receiptUrl && window.open(row.receiptUrl)}
                            >
                                <div className="bi-invoice-card-top">
                                    <span className="bi-invoice-card-period">{moment(row.date).format('D MMM YYYY')}</span>
                                    <span className="bi-invoice-card-total">{money(row.amountGBP)}</span>
                                </div>
                                <div className="bi-invoice-card-bottom">
                                    <span>{addedOf(row)} added{row.automatic ? ' · auto' : ''}</span>
                                    {row.receiptUrl && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalCount >= itemPerPageLimit && (
                        <div className="settings-pagination">
                            <Pagination
                                activePage={activePage}
                                itemsCountPerPage={itemPerPageLimit}
                                totalItemsCount={totalCount}
                                pageRangeDisplayed={3}
                                onChange={(page) => setActivePage(page)}
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="bi-empty">
                    <div className="bi-empty-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                    </div>
                    <p>{notice || 'No top-ups yet'}</p>
                    <span>
                        {notice
                            ? 'Your message balance is topped up and billed by the salon.'
                            : 'Receipts appear here when you top up your message balance'}
                    </span>
                </div>
            )}
        </div>
    );
}

function mapStateToProps(state) {
    return { ...state };
}

export default connect(mapStateToProps)(CreditPurchases);
